import { useCallback, useEffect, useRef, useState } from 'react'
import {
  applyPreferences,
  DEFAULT_PREFERENCES,
  loadPreferences,
  savePreferences,
  type A11yPreferences,
} from './preferences'

/** Fired on window whenever preferences change (detail: A11yPreferences). */
export const A11Y_CHANGE_EVENT = 'gia:a11y-change'

let readableFontRequested = false
function loadReadableFont() {
  if (readableFontRequested) return
  readableFontRequested = true
  // Only visitors who switch the option on download the font.
  void import('@fontsource/atkinson-hyperlegible/latin-400.css')
  void import('@fontsource/atkinson-hyperlegible/latin-700.css')
}

/**
 * Accessibility preferences: read from storage on mount (the inline bootstrap
 * has already applied them to <html>), applied and saved on every change.
 * Also owns the two behaviours CSS can't do alone: loading the readable font
 * and moving the reading guide with the pointer.
 */
export function useA11yPreferences() {
  const [prefs, setPrefs] = useState<A11yPreferences>(DEFAULT_PREFERENCES)
  const prefsRef = useRef(prefs)

  useEffect(() => {
    const stored = loadPreferences()
    prefsRef.current = stored
    setPrefs(stored)
  }, [])

  const update = useCallback((patch: Partial<A11yPreferences>) => {
    const next = { ...prefsRef.current, ...patch }
    prefsRef.current = next
    setPrefs(next)
    applyPreferences(next)
    savePreferences(next)
    window.dispatchEvent(new CustomEvent(A11Y_CHANGE_EVENT, { detail: next }))
  }, [])

  const reset = useCallback(() => update(DEFAULT_PREFERENCES), [update])

  useEffect(() => {
    if (prefs.readableFont) loadReadableFont()
  }, [prefs.readableFont])

  // Reading guide: track the pointer (mouse/pen only), one update per frame.
  useEffect(() => {
    if (!prefs.readingGuide) return
    const root = document.documentElement
    let frame = 0
    function onMove(event: PointerEvent) {
      if (event.pointerType === 'touch') return
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() => root.style.setProperty('--a11y-guide-y', `${event.clientY}px`))
    }
    window.addEventListener('pointermove', onMove, { passive: true })
    return () => {
      window.removeEventListener('pointermove', onMove)
      cancelAnimationFrame(frame)
      root.style.removeProperty('--a11y-guide-y')
    }
  }, [prefs.readingGuide])

  return { prefs, update, reset }
}
